import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Fab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentsIcon from '@mui/icons-material/Payments';
import { format } from 'date-fns';
import { useIsMobile } from '@/hooks/useIsMobile';
import { DataTable, type DataTableColumn, type QuickFilter } from '@/components/common';
import { ReturnFormDialog } from '@/components/inventory';
import {
  useReturns,
  useUpdateReturnStatus,
  type ReturnToSupplier,
  type ReturnStatus,
} from '@/hooks';
import { useNotify } from '@/stores/notificationStore';
import { dateFnsLocale } from '@/utils/locale';

type TabValue = 'all' | ReturnStatus;

// Status → MUI chip colour. No user-visible strings, so module scope is fine.
const statusColors: Record<ReturnStatus, 'default' | 'info' | 'primary' | 'success'> = {
  created: 'default',
  shipped: 'info',
  received: 'primary',
  refunded: 'success',
  credited: 'success',
};

const STATUS_ORDER: ReturnStatus[] = ['created', 'shipped', 'received', 'refunded', 'credited'];

export default function ReturnsPage() {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isMobile = useIsMobile();

  const returnsQuery = useReturns();
  const updateStatus = useUpdateReturnStatus();

  const returns = useMemo(() => returnsQuery.data ?? [], [returnsQuery.data]);

  const [tab, setTab] = useState<TabValue>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuReturn, setMenuReturn] = useState<ReturnToSupplier | null>(null);

  const statusLabels: Record<ReturnStatus, string> = {
    created: t('returns.status.created'),
    shipped: t('returns.status.shipped'),
    received: t('returns.status.received'),
    refunded: t('returns.status.refunded'),
    credited: t('returns.status.credited'),
  };

  const filtered = useMemo(
    () => (tab === 'all' ? returns : returns.filter((r) => r.status === tab)),
    [returns, tab],
  );

  const statusCounts = useMemo(() => {
    const counts: Record<TabValue, number> = {
      all: returns.length,
      created: 0,
      shipped: 0,
      received: 0,
      refunded: 0,
      credited: 0,
    };
    for (const r of returns) counts[r.status]++;
    return counts;
  }, [returns]);

  const quickFilters: QuickFilter[] = [
    { label: `${tc('all')} (${String(statusCounts.all)})`, value: 'all' },
    ...STATUS_ORDER.map((s) => ({
      label: `${statusLabels[s]} (${String(statusCounts[s])})`,
      value: s,
    })),
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, r: ReturnToSupplier) => {
    setAnchorEl(event.currentTarget);
    setMenuReturn(r);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuReturn(null);
  };

  const handleTransition = (r: ReturnToSupplier, status: ReturnStatus) => {
    updateStatus.mutate(
      { id: r.id, status },
      {
        onSuccess: () => notify.success(t('returns.statusUpdated'), statusLabels[status]),
        onError: () => notify.error(tc('error'), t('returns.statusUpdateFailed')),
      },
    );
    handleMenuClose();
  };

  const formatPrice = (cents: number) => `€${(cents / 100).toFixed(2)}`;

  const columns: DataTableColumn<ReturnToSupplier>[] = [
    {
      header: t('returns.colSupplier'),
      renderCell: (r) => (
        <>
          <Typography variant="body2" fontWeight={600}>
            {r.supplierName}
          </Typography>
          {r.reason && (
            <Typography variant="caption" color="text.secondary">
              {r.reason}
            </Typography>
          )}
        </>
      ),
    },
    {
      header: t('returns.colLines'),
      renderCell: (r) => (
        <Typography variant="body2">{t('returns.linesCount', { count: r.lines.length })}</Typography>
      ),
    },
    {
      header: t('returns.colTotal'),
      align: 'right',
      renderCell: (r) => (
        <Typography variant="body2" fontWeight={600}>
          {formatPrice(r.totalCents)}
        </Typography>
      ),
    },
    {
      header: t('returns.colStatus'),
      renderCell: (r) => (
        <Chip label={statusLabels[r.status]} size="small" color={statusColors[r.status]} />
      ),
    },
    {
      header: t('returns.colCreated'),
      renderCell: (r) => (
        <Typography variant="body2">
          {format(new Date(r.createdAt), 'd.M.yyyy', { locale: dateFnsLocale() })}
        </Typography>
      ),
    },
    {
      header: '',
      align: 'right',
      renderCell: (r) =>
        r.status === 'refunded' || r.status === 'credited' ? null : (
          <Tooltip title={tc('moreOptions')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e, r);
              }}
              aria-label={tc('moreOptions')}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {t('returns.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('returns.subtitle')}
          </Typography>
        </Box>
        {!isMobile && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
            {t('returns.createReturn')}
          </Button>
        )}
      </Box>

      <Card>
        <Box sx={{ px: 2, pt: 2 }}>
          <DataTable<ReturnToSupplier>
            columns={columns}
            data={filtered}
            getRowKey={(r) => r.id}
            quickFilters={quickFilters}
            activeFilter={tab}
            onFilterChange={(value) => setTab(value as TabValue)}
            paperWrapper={false}
            loading={returnsQuery.isLoading}
            emptyMessage={t('returns.noReturns')}
            emptyDescription={t('returns.noReturnsDesc')}
            renderMobileCard={(r) => (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {r.supplierName}
                  </Typography>
                  <Chip label={statusLabels[r.status]} size="small" color={statusColors[r.status]} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {formatPrice(r.totalCents)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(r.createdAt), 'd.M.yyyy', { locale: dateFnsLocale() })}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </Box>
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {menuReturn?.status === 'created' && (
          <MenuItem onClick={() => menuReturn && handleTransition(menuReturn, 'shipped')}>
            <LocalShippingIcon fontSize="small" sx={{ mr: 1 }} />
            {t('returns.markShipped')}
          </MenuItem>
        )}
        {menuReturn?.status === 'shipped' && (
          <MenuItem onClick={() => menuReturn && handleTransition(menuReturn, 'received')}>
            <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
            {t('returns.markReceived')}
          </MenuItem>
        )}
        {menuReturn?.status === 'received' && (
          <MenuItem onClick={() => menuReturn && handleTransition(menuReturn, 'refunded')}>
            <PaymentsIcon fontSize="small" sx={{ mr: 1 }} />
            {t('returns.markRefunded')}
          </MenuItem>
        )}
        {menuReturn?.status === 'received' && (
          <MenuItem onClick={() => menuReturn && handleTransition(menuReturn, 'credited')}>
            <PaymentsIcon fontSize="small" sx={{ mr: 1 }} />
            {t('returns.markCredited')}
          </MenuItem>
        )}
      </Menu>

      {isMobile && (
        <Fab
          color="primary"
          size="medium"
          onClick={() => setFormOpen(true)}
          sx={{ position: 'fixed', bottom: 72, right: 16, zIndex: 1050 }}
          aria-label={t('returns.createReturn')}
        >
          <AddIcon />
        </Fab>
      )}

      <ReturnFormDialog open={formOpen} onClose={() => setFormOpen(false)} />
    </Box>
  );
}
